import { __makeTemplateObject } from '../../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { PlayFilledIcon } from '@elseu/sdu-titan';
import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useResize } from '../../utilities/useResize/useResize.js';

var VideoWrapper = styled.div.attrs({
  'data-test-id': 'video'
})(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  position: relative;\n  padding-bottom: 56.25%;\n  height: 0;\n  overflow: hidden;\n  width: 100%;\n  max-width: 1200px;\n  margin: 0 auto;\n"], ["\n  position: relative;\n  padding-bottom: 56.25%;\n  height: 0;\n  overflow: hidden;\n  width: 100%;\n  max-width: 1200px;\n  margin: 0 auto;\n"])));
var StyledIframe = styled.iframe(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n"], ["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n"])));
var StyledVideo = styled.video(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  object-fit: cover;\n"], ["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  object-fit: cover;\n"])));
var VideoContainer = styled.div(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  cursor: pointer;\n"], ["\n  cursor: pointer;\n"])));
var Thumbnail = styled.img(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  object-fit: cover;\n"], ["\n  position: absolute;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  object-fit: cover;\n"])));
var IconWrapper = styled.div(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  color: #fff;\n  font-size: 72px;\n"], ["\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  color: #fff;\n  font-size: 72px;\n"])));
/**
 * Custom hook to determine the quality of a YouTube video thumbnail based on the container dimensions.
 * It checks whether a max resolution thumbnail is available for the video and chooses the best quality
 * based on the available resolution and the container dimensions.
 *
 * @param {object} params - The parameters object.
 * @param {string | undefined} params.youtubeId - The YouTube video ID.
 * @param {{width: number, height: number}} params.dimensions - The dimensions of the thumbnail container.
 * @returns {'default' | 'mqdefault' | 'hqdefault' | 'maxresdefault' | 'sddefault'} The thumbnail quality.
 */
var useThumbnailQuality = function (_a) {
  var youtubeId = _a.youtubeId,
    dimensions = _a.dimensions;
  var _b = useState(false),
    hasMaxRes = _b[0],
    setHasMaxRes = _b[1];
  useEffect(function () {
    var image = new Image();
    image.onload = function () {
      /** If the image is 120px wide, it means that the max resolution thumbnail is not available. */
      if (image.naturalWidth === 120) {
        setHasMaxRes(false);
      } else {
        setHasMaxRes(true);
      }
    };
    image.onerror = function () {
      setHasMaxRes(false);
    };
    image.src = "https://img.youtube.com/vi/".concat(youtubeId, "/maxresdefault.jpg");
  }, [youtubeId]);
  var thumbnailQuality = useMemo(function () {
    if (dimensions.width < 320) return 'default';
    if (dimensions.width < 480) return 'mqdefault';
    if (dimensions.width < 640) return 'hqdefault';
    if (dimensions.width < 800 || hasMaxRes) return 'maxresdefault';
    return 'sddefault';
  }, [dimensions, hasMaxRes]);
  return thumbnailQuality;
};
/**
 * A component to display YouTube videos with a lazy loading feature.
 * It initially shows a thumbnail image of the YouTube video with a play button.
 * Once clicked, it swaps the thumbnail with the actual YouTube iframe to load and play the video.
 *
 * @param {VideoProps} props - The properties that define the video sources.
 * @param {string} [props.youtubeId] - The YouTube ID of the video.
 * @returns {JSX.Element} The rendered Video component.
 */
var YoutubeVideo = function (_a) {
  var youtubeId = _a.youtubeId;
  var _b = useState(false),
    isYoutubeLoaded = _b[0],
    setIsYoutubeLoaded = _b[1];
  var _c = useResize(),
    ref = _c[0],
    dimensions = _c[1];
  var handleVideoClick = function () {
    setIsYoutubeLoaded(true);
  };
  var thumbnailQuality = useThumbnailQuality({
    youtubeId: youtubeId,
    dimensions: dimensions
  });
  return jsx(VideoContainer, {
    ref: ref,
    role: "button",
    onClick: handleVideoClick,
    onKeyDown: function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        handleVideoClick();
      }
    },
    tabIndex: 0,
    children: isYoutubeLoaded ? jsx(StyledIframe, {
      title: "YouTube video player for video with ID: ".concat(youtubeId),
      src: "https://www.youtube-nocookie.com/embed/".concat(youtubeId),
      frameBorder: 0,
      allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
      allowFullScreen: true,
      loading: "lazy"
    }) : jsxs(Fragment, {
      children: [jsx(Thumbnail, {
        src: "https://img.youtube.com/vi/".concat(youtubeId, "/").concat(thumbnailQuality, ".jpg"),
        alt: "Thumbnail for YouTube video with ID: ".concat(youtubeId)
      }), jsx(IconWrapper, {
        children: jsx(PlayFilledIcon, {})
      })]
    })
  });
};
/**
 * A component to display videos with a lazy loading feature.
 *
 * @param {VideoProps} props - The properties that define the video sources.
 * @param {string} [props.youtubeId] - The YouTube ID of the video.
 * @param {Object} [props.media] - The media object containing the URL of the video.
 * @param {string} props.media.url - The URL of the video.
 * @returns {JSX.Element} The rendered Video component.
 */
var Video = function (_a) {
  var youtubeId = _a.youtubeId,
    media = _a.media;
  return jsxs(VideoWrapper, {
    children: [youtubeId && jsx(YoutubeVideo, {
      youtubeId: youtubeId
    }), media && jsx(StyledVideo, {
      src: media.url,
      autoPlay: true,
      loop: true,
      muted: true,
      playsInline: true
    })]
  });
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;

export { Video, VideoWrapper };
//# sourceMappingURL=Video.js.map
